import FolderIcon from '@mui/icons-material/FolderOpen'
import qs from 'query-string'
import { Box, Button, Chip, CircularProgress } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { BLUE } from '~/shared/lib/theme'
import { useMemo, type FC } from 'react'

import { useUpdateCase } from '~/entities/case'
import { useNavigate } from 'react-router'
import { findMessageIdentifier } from './utils'
import { useManageSelectedTodo } from '../useManageSelectedTodo'
import { BffCrmPersonChatRetrieveResponse } from '@fireflyhealth/api'

type Communication =
  BffCrmPersonChatRetrieveResponse['communications']['results'][number]['content_object']

type ChatCommunication = Extract<Communication, { kind: 'chat' }>

type PhoneCommunication = Extract<Communication, { kind: 'phone' }>
interface Props {
  message:
    | (Pick<ChatCommunication, 'id' | 'uid' | 'kind'> & {
        case_relations: {
          case: Pick<ChatCommunication['case_relations'][number]['case'], 'id'> & {
            category: Pick<
              ChatCommunication['case_relations'][number]['case']['category'],
              'id' | 'title'
            >
            relations: (Pick<
              ChatCommunication['case_relations'][number]['case']['relations'][number],
              'object_id'
            > & {
              content_type: Pick<
                ChatCommunication['case_relations'][number]['case']['relations'][number]['content_type'],
                'model'
              >
            })[]
          }
        }[]
      })
    | (Pick<PhoneCommunication, 'id'> & {
        case_relations: {
          case: Pick<PhoneCommunication['case_relations'][number]['case'], 'id'> & {
            category: Pick<
              PhoneCommunication['case_relations'][number]['case']['category'],
              'id' | 'title'
            >
            relations: (Pick<
              PhoneCommunication['case_relations'][number]['case']['relations'][number],
              'object_id'
            > & {
              content_type: Pick<
                PhoneCommunication['case_relations'][number]['case']['relations'][number]['content_type'],
                'model'
              >
            })[]
          }
        }[]
      })
  threadUid: string | null
  canUncase: boolean
}

const CaseInfoChips: FC<{
  messageId: number
  threadUid: string | null
  cases: Pick<Props['message']['case_relations'][number]['case'], 'id' | 'category'>[]
}> = props => {
  const { navigateToCase } = useManageSelectedTodo()
  // Early return if no cases for this message
  if (!props.cases.length) return null

  return (
    <Box
      sx={{
        flexDirection: 'column',
        display: 'flex',
        alignItems: 'flex-start',
        position: 'relative',
      }}
    >
      {props.cases.map((caseObj, i) => {
        return (
          <Chip
            key={i}
            icon={<FolderIcon style={{ color: BLUE[600] }} />}
            label={caseObj.category.title}
            variant="outlined"
            size="small"
            onClick={() => navigateToCase(caseObj.id)}
            sx={{
              color: 'primary.600',
              borderColor: 'transparent',
              backgroundColor: 'transparent',
              display: 'flex',
              padding: 0.5,
            }}
          />
        )
      })}
    </Box>
  )
}

const MessageInfo: FC<Props> = props => {
  // Message information and controls for the actions a user can take on a message
  // e.g. triaging to Clinical vs. Operational, adding to a Case
  const { threadUid } = props
  const { selectedCaseId } = useManageSelectedTodo()

  const { mutate: updatePatientCase, isPending: isLoading } = useUpdateCase()
  const { currentCaseMatch, messageIdentifier } = useMemo(() => {
    let currentCaseMatch: Props['message']['case_relations'][number]['case'] | null = null
    const messageIdentifier = findMessageIdentifier(props.message)

    if (selectedCaseId && props.message.case_relations) {
      // Use case info to find the current case match and its unlinkability
      currentCaseMatch =
        props.message.case_relations
          .map(relation => relation.case)
          .find(caseObj => caseObj.id === selectedCaseId) ?? null
    }
    return { currentCaseMatch, messageIdentifier }
  }, [props.message, selectedCaseId])

  const navigate = useNavigate()

  // This function is used to clear search results and yellow highlighting
  // once the last message linked to case has been removed
  const handleCloseCaseSearch = () => {
    const existingQS = qs.parse(location.search)
    delete existingQS['caseId']
    delete existingQS['action']
    // Pass the pathname explicitly: a pathless partial resolves against the
    // matched route, not the current URL, and would drop splat segments.
    void navigate({
      pathname: location.pathname,
      search: qs.stringify(existingQS),
    })
  }

  const handleUnlinkCase = () => {
    if (!currentCaseMatch) return
    const { id, relations } = currentCaseMatch
    updatePatientCase(
      {
        id,
        body: {
          relations: relations
            .filter(
              relation =>
                !(
                  // Matches on model alone, not app_label; collisions are unlikely.
                  relation.content_type.model === messageIdentifier.type &&
                  relation.object_id === messageIdentifier.id
                ),
            )
            .map(relation => ({
              content_type: relation.content_type.model,
              object_id: relation.object_id,
            })),
        },
      },
      {
        // Only once the server has confirmed the unlink: dropping the filter first would hide
        // the row and move the view on while the relation still stands on a refused request.
        onSuccess: () => {
          // The chat view is filtered by `caseId`, so once this message is no longer
          // linked to the case it would disappear from view. For phone calls, the
          // user needs to keep seeing the row to continue the workflow
          // (Unlink from Case -> Unlink from Person), so drop the filter. Also drop
          // it when this was the case's last relation, since the filtered view would
          // otherwise be empty.
          const isPhoneCall = messageIdentifier.type === 'phonecall'
          if (isPhoneCall || relations.length == 1) {
            handleCloseCaseSearch()
          }
        },
      },
    )
  }

  return (
    <Box sx={{ display: 'flex', position: 'relative', flexGrow: 1 }}>
      {/* If already cased
      Display the case names (not parent)
      If not already cased
      Show a "Case" button */}
      {!currentCaseMatch ? (
        <CaseInfoChips
          messageId={props.message.id}
          threadUid={threadUid}
          cases={props.message.case_relations.map(relation => relation.case)}
        />
      ) : null}
      {/* If there is a selected Case, this message is linked to it, and it is not linked to a thread, offer the option to un-link it */}
      {currentCaseMatch && props.canUncase ? (
        <Box sx={{ marginLeft: 1 }}>
          <Button
            onClick={handleUnlinkCase}
            size="small"
            startIcon={isLoading ? <CircularProgress size={12} /> : <ClearIcon />}
            sx={{
              padding: '0',
              opacity: '1',
              color: 'secondary.light',
              background: 'none',
              border: 'none',
            }}
          >
            Remove from Case
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}

export default MessageInfo
